// deps
import React, { ReactElement, memo, useState, useContext, useCallback } from "react";

// internal-deps
import { isEmpty, ScreenTransition, useEffectOnce } from "@swiggy-private/core-ui";

// utils
import { RouteProps } from "@payment-client/interfaces/Router";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import {
    CheckoutContext,
    CheckoutActionTypes,
    recordNREvent,
    NEWRELIC_TABLES,
    BillToCompanyContext,
} from "@swiggy-private/checkout-core";
import { CroutonContext } from "@payment-client/contexts/CroutonContext";

// components
import PaymentUIWrapper from "./components/payment-renderer/payment-ui-wrapper";
import AutoPayErrorScreen from "./components/autopay-error-screen/AutoPayErrorScreen";

// hooks
import useForward from "@payment-client/hooks/UseForward";
import useShowPaymentsState from "@payment-client/hooks/UseShowPaymentsState";
import useCrouton from "@payment-client/hooks/UseCrouton";
import { useTransformPaymentResponse } from "@payment-client/hooks/UseTransformPaymentResponse";

// helpers
import { getProcessOrderState, cleanProcessOrderState } from "@payment-client/helpers/CheckoutStateHelper";
import { MARKETPLACE_BUSINESS_LINE } from "@swiggy-private/payment-types";
import { getBusinessLine } from "@payment-client/helpers/Misc";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { RequestCartKeyForPayOnDeliveryErrorPayload } from "@payment-client/interfaces/Checkout";
import { isBusinessLinePayOnDelivery } from "@payment-client/helpers/Misc";
import { getTurboPaymentEligibilityMeta } from "@payment-client/helpers/TurboPayment";
import { WithContextProviders } from "./hoc";
import { getPaymentCodeFromOrderData } from "@payment-client/helpers/PaymentHelper";
import { shouldPollForTxnStatus } from "@payment-client/helpers/PollingHelpers";

// When we render only turbo payment widget then showpayments is not inside a router
// so it will use a different set of props
type OnlyTurboPaymentWidgetProps = {
    /** this value if provided, it has to be true always */
    isTurboPaymentMethod: true;
    /**
     * This is a unique value which we listen to, this value should change if there's a possibility of payment related things to change
     *
     * For Instamart/Handpick etc businessline, pass in the paymentLinkId value that is generated, this is the same value u are passing  onto the PaymentMain component in cartkey header */
    turboPayUniqueId: string;
    /** React children */
    children: RouteProps["children"];

    /** This prop is used to show the new turbo widget */
    /** isTurboV2 will be removed across the repo after cart 2.0 XP */
    isTurboV2?: boolean;

    /** This prop is used to show the bill to company toggle */
    isBillToCompanySelected?: boolean;

    /** This prop is used to handle the color mode for the turbo payment only for scenes as the scenes cart has dark mode*/
    forceColorMode?: "light" | "dark";
};

export type ShowPaymentsProps = RouteProps | OnlyTurboPaymentWidgetProps;

export const ShowPayments = memo(function ShowPayments(props: ShowPaymentsProps): ReactElement {
    const { setCheckoutState } = useContext(CheckoutContext);
    const { isSelected } = useContext(BillToCompanyContext);

    const turboPaymentEligibilityMeta = getTurboPaymentEligibilityMeta({
        ...props,
        isBillToCompanySelected: isSelected,
    });
    const [showPaymentState, reloadPayments, refetchPayments] = useShowPaymentsState(turboPaymentEligibilityMeta);
    const [isPaymentMethodSelectedForOnce, setIsPaymentMethodSelectedForOnce] = useState(false);

    const isTurboPaymentMethod = "isTurboPaymentMethod" in props && props.isTurboPaymentMethod;
    const forceColorMode = "forceColorMode" in props && props.forceColorMode;

    const showPaymentContextValue = useTransformPaymentResponse({
        showPaymentState,
        isTurboPaymentMethod,
        reloadPayments,
        refetchPayments,
        isPaymentMethodSelectedForOnce,
        setIsPaymentMethodSelectedForOnce,
    });

    const handleCheckoutStateBasedOnProcessOrderState = useCallback(() => {
        const processOrderState = getProcessOrderState();
        if (!processOrderState || processOrderState.type !== CheckoutActionTypes.PROCESS_ORDER) {
            return;
        }

        // in case user kills the app after swiggy money recharge attempt,
        // the process order state will contain stale SM order id
        // This is required to reset the stale SM data, on payment page launch
        if (processOrderState?.isSwiggyMoney && getBusinessLine() !== MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY) {
            cleanProcessOrderState();
            return;
        }
        const methodName = getPaymentCodeFromOrderData(processOrderState.state?.placeOrderData) || "";

        if (shouldPollForTxnStatus(processOrderState.state.order, methodName)) {
            setCheckoutState({
                type: CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER,
                payload: {
                    order: processOrderState.state.order,
                    placeOrderData: processOrderState.state.placeOrderData,
                    startTime: processOrderState.state.startTime,
                },
            });
        } else {
            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_ORDER,
                payload: {
                    orderId: processOrderState.orderId,
                    order: processOrderState.state.order,
                    placeOrderData: processOrderState.state.placeOrderData,
                },
            });
        }
    }, [setCheckoutState]);

    // To handle the web back button handling from Apay, Netbanking
    useEffectOnce(() => {
        handleCheckoutStateBasedOnProcessOrderState();
        cleanProcessOrderState();
        try {
            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "ShowPaymentsCleanState",
                orderId: getProcessOrderState()?.orderId || "",
            });
        } catch {
            /* No-op */
        }
    });

    const forward = useForward();
    const children = Array.isArray(props.children) && props.children.length === 1 ? props.children[0] : props.children;

    const { loadingPayments, paymentResponse } = showPaymentContextValue;

    //Payments page crouton
    const croutonContextValue = useCrouton();

    if (isBusinessLinePayOnDelivery() && !isEmpty(showPaymentState.error)) {
        const errorObject = showPaymentState?.error as ApiResponse<RequestCartKeyForPayOnDeliveryErrorPayload>;

        return (
            <CroutonContext.Provider value={croutonContextValue}>
                <ShowPaymentContext.Provider value={showPaymentContextValue}>
                    <PaymentUIWrapper withCoupon={false} setWithCoupon={(): void => void 0}>
                        <AutoPayErrorScreen
                            statusCode={errorObject.statusCode}
                            title={errorObject?.data?.title}
                            message={errorObject?.data?.subTitle}
                        />
                    </PaymentUIWrapper>
                </ShowPaymentContext.Provider>
            </CroutonContext.Provider>
        );
    }

    return (
        <ShowPaymentContext.Provider value={showPaymentContextValue}>
            {loadingPayments || !paymentResponse ? (
                children
            ) : (
                <WithContextProviders
                    paymentResponse={paymentResponse}
                    isTurboPaymentMethod={isTurboPaymentMethod}
                    forceColorMode={forceColorMode || "light"}
                >
                    <CroutonContext.Provider value={croutonContextValue}>
                        {/* When rendering turbo widget we dont need screen transition we only need context, otherwise UI will break */}
                        {isTurboPaymentMethod ? (
                            children
                        ) : (
                            <ScreenTransition forward={forward}>{children as ReactElement}</ScreenTransition>
                        )}
                    </CroutonContext.Provider>
                </WithContextProviders>
            )}
        </ShowPaymentContext.Provider>
    );
});
